import { curry, curryN, negate, omitBy } from 'lodash/fp';

export { default as evolve } from 'ramda/src/evolve';

type FnShortCircuit<T extends (...args: any[]) => any> = (
	fns: T[],
	...args: Parameters<T>
) => ReturnType<T>;

/**
 * Returns all key and values from obj2 which are different from obj1
 */
export function objDiff(
	obj1: Record<string, unknown>,
	obj2: Record<string, unknown>
): Partial<typeof obj2> {
	return omitBy((value, key) => key in obj1 && obj1[key] === value, obj2);
}

/**
 * Equivalent to `&&` but for functions. Takes array of functions and applies to them subsequent arguments. Yields the
 * first falsy result of the functions, or, when all functions returns truthy, the last result.
 */
export const overAnd: FnShortCircuit<any> = curryN(2, function overAnd(fns, ...args) {
	let result;
	// eslint-disable-next-line no-cond-assign
	for (let fn, i = 0; (fn = fns[i]); i += 1) {
		// eslint-disable-next-line no-cond-assign
		if (!(result = fn(...args))) break;
	}
	return result;
});

/**
 * Equivalent to `||` but for functions. Takes array of functions and applies to them subsequent arguments. Yields the
 * first truthy result of the functions, or, when all functions returns falsy, the last result.
 */
export const overOr: FnShortCircuit<any> = curryN(2, function overOr(fns, ...args) {
	let result;
	// eslint-disable-next-line no-cond-assign
	for (let fn, i = 0; (fn = fns[i]); i += 1) {
		// eslint-disable-next-line no-cond-assign
		if ((result = fn(...args))) break;
	}
	return result;
});

/**
 * Renames fields in data according to fromToMap
 * @param {Record<string, string>} fromToMap - map of fields name {originalName => newName}
 * @param {object} data - object which fields will be renamed
 */
export const renameFields = curry(
	(fromToMap: Record<string, string>, data: Record<string, unknown>) =>
		Object.keys(data).reduce((acc, key) => {
			acc[fromToMap[key] || key] = data[key];
			return acc;
		}, {})
);

/**
 * Like `throw` statement, but usable in expressions
 */
export function rethrow(error: any): never {
	throw error;
}

/**
 * Captures subsequent call arguments until is has sufficient arguments to invoke provided setter.
 */
export function scan<T>(
	scanner: (...args: T[]) => any,
	bufferLength = scanner.length
): (arg: T) => ReturnType<typeof scanner> | undefined {
	const buffer: T[] = [];
	return (arg) => {
		buffer.push(arg);
		return buffer.length < bufferLength ? undefined : scanner(buffer.shift() as T, ...buffer);
	};
}

/**
 * Converts input to opposite boolean value
 */
export const toggle: (input: any) => boolean = negate(Boolean);
